import { html, getAll, get } from '@bigyouth/base'
import setAttributes from '../utils/setAttributes'
import { create as createPipe } from '../utils/sigpipe'

const VALID_EMAIL = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/ // eslint-disable-line
const VALID_TEL = /^(?:(?:\+|00)33[\s.-]{0,3}(?:\(0\)[\s.-]{0,3})?|0)[1-9](?:(?:[\s.-]?\d{2}){4}|\d{2}(?:[\s.-]?\d{3}){2})$/ // eslint-disable-line

export const FORM_ACTIONS = {
  SUBMIT: 'FORM_SUBMIT',
  ERROR: 'FORM_ERROR',
}

/**
 * FormValidation makes sure that specified inputs are correctly filled before submitting the form.
 * By default, FormValidation trigger the native action, if a form ID is specified via its props,
 * the native action is overrided and an event is triggered with the corresponding id.
 *
 * data-validation-type="[FIELD_TYPE]" should be added on the js-form-validation-field for specific
 * tests. It can be the following: email; tel; date; checkbox; radio; length.
 * Note: for radios, add the attributes on the first one.
 *
 * !! This feature is a template and can be modified to suit the project needs.
 *
 * Props:
 * isCustomSubmit(boolean, optional): if the component should inject an id and trigger an event on submit
 */
export default {
  name: 'form-validation',

  hasError: false,

  ui: {
    'fieldWrappers[]': '[js-form-validation-field-wrapper]',
    'fields[]': '[js-form-validation-field]',
  },

  events: [
    {
      on: 'submit',
      handle: 'handleSubmit',
    },
    {
      target: 'fields',
      on: 'change',
      handle (e) {
        this.removeError(e.target)
      },
    },
  ],

  componentDidMount () {
    this.el.setAttribute('novalidate', '')

    if (this.props && this.props.isCustomSubmit) {
      this.pipe = createPipe()
      setAttributes({ dataFormId: `${this.pipe.id}` })(this.el)
    }

    getAll('[js-form-validation-field]', this.el).forEach(field => {
      // on blur
      field.addEventListener('blur', (e) => {
        const field = e.target
        this.removeError(field)
        this.globalCheck(field)
      })

      // on change
      field.addEventListener('change', (e) => {
        this.removeError(e.target)
      })

      // on input
      field.addEventListener('input', (e) => {
        this.removeError(e.target)

        if (get('[js-form-error]')) {
          get('[js-form-error]').remove()
        }
      })
    })

    // disable autofill password in account only hack found
    const passwordField = get('[js-form-validation-password]', this.el)
    setTimeout(() => {
      if (passwordField) {
        passwordField.value = ''
      }
    }, 1500)
  },

  handleSubmit (e) {
    // Reset errors
    this.hasError = false
    this.removeErrors()

    this.ui.fields.forEach((field) => {
      this.globalCheck(field)
    })

    const formData = this.ui.fields.reduce((acc, field) => {
      if (field.type === 'radio') {
        const selectedRadio = get(
          `input[name="${field.name}"]:checked`,
          this.el,
        )
        acc[field.name] = selectedRadio ? selectedRadio.value : ''
      } else if (field.type === 'checkbox') {
        acc[field.name] = field.checked ? 1 : 0
      } else {
        acc[field.name] = field.value
      }
      return acc
    }, {})

    if (this.hasError) {
      if (e) {
        e.preventDefault()
      }

      if (this.props && this.props.isCustomSubmit) {
        this.pipe.write({ action: FORM_ACTIONS.ERROR, formData })
      }
    } else if (this.props && this.props.isCustomSubmit) {
      if (e) {
        e.preventDefault()
      }

      this.pipe.write({ action: FORM_ACTIONS.SUBMIT, formData })
    }
  },

  globalCheck (field) {
    const wrapper = field.closest('[js-form-validation-field-wrapper]') || field.parentNode

    if (field.required) {
      if (field.value !== '') {
        this.checkField(field, wrapper)
      } else {
        this.hasError = true
        wrapper.classList.add('has-error')
        wrapper.appendChild(
          this.createErrorMessage('Ce champ est obligatoire'),
        )
      }
    } else {
      if (field.value !== '') {
        this.checkField(field, wrapper)
      }
    }
  },

  checkField (field, wrapper) {
    const validationtype = field.getAttribute('data-validation-type')
    let isValid = true
    let errorLabel = 'Champ incorrect'

    switch (validationtype) {
      case 'email':
        isValid = VALID_EMAIL.test(field.value)
        if (!isValid) errorLabel = 'Format d\'email incorrect'
        break

      case 'tel':
        isValid = VALID_TEL.test(field.value)
        if (!isValid) errorLabel = 'Format de téléphone incorrect'
        break

      case 'checkbox':
        isValid = field.checked
        if (!isValid) errorLabel = 'Ce champ est obligatoire'
        break

      case 'password':
        /* eslint-disable-next-line */
        isValid = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d\/@$!%*#?&]{8,4096}$/.test(field.value)
        if (!isValid) errorLabel = 'Format du mot de passe invalide'
        break

      case 'passwordConfirm': {
        const passwordValue = get('[js-form-validation-password]').value
        if (passwordValue !== field.value) {
          isValid = false
        }

        if (!isValid) errorLabel = 'Les deux mots de passe doivent être identiques'
        break
      }

      case 'emailConfirm': {
        const emailValue = get('[js-form-validation-email]').value
        if (emailValue !== field.value) {
          isValid = false
        }

        if (!isValid) errorLabel = 'Les deux adresses mail doivent être identiques'
        break
      }

      case 'radio':
        isValid = get(`input[name="${field.name}"]:checked`, this.el)
        if (!isValid) errorLabel = 'Veuillez sélectionner un choix'
        break
    }

    if (!isValid) {
      wrapper.classList.add('has-error')
      wrapper.appendChild(this.createErrorMessage(errorLabel))
    }

    this.hasError = this.hasError || !isValid
  },

  checkFieldLength (field) {
    const minChar = parseInt(field.getAttribute('minlength')) || 0
    const maxChar = parseInt(field.getAttribute('maxlength')) || Infinity

    return field.value.length <= maxChar && field.value.length >= minChar
  },

  createErrorMessage (content) {
    const node = document.createElement('div')
    node.innerHTML = html`
      <div class="form__error" js-form-validation-error>${content}</div>
    `

    return node.firstElementChild
  },

  removeErrors () {
    this.ui.fields.forEach(this.removeError)
    if (get('[js-form-error]')) {
      get('[js-form-error]').remove()
    }
  },

  removeError (field) {
    const wrapper =
      field.closest('[js-form-validation-field-wrapper]') || field.parentNode

    if (wrapper.classList.contains('has-error')) {
      const errorLabel = get('[js-form-validation-error]', wrapper)

      wrapper.classList.remove('has-error')

      if (errorLabel) errorLabel.remove()
    }
  },
}
